import { num } from './format.js';

/*
 * Joining and email (docs/23 §3.6, Saif 20 Oct 2026): words for the welcome points (they arrive with the first
 * collected purchase), receipt claims that include them, and the email confirmation. Pure, so they're tested.
 */

/** An ApiError's status and code, read by shape (lib/api.ts needs the browser, these don't). */
const err = (e: unknown): { status: number; code: string; message: string } | null =>
  e && typeof e === 'object' && typeof (e as { status?: unknown }).status === 'number' ? (e as { status: number; code: string; message: string }) : null;

/** "100 welcome points arrive with your first order", or '' when there are none to come. */
/**
 * The join card's benefit lines (Saif, 24 Oct 2026), in order, from the settings: the welcome points (skipped at 0), the
 * rate, the two reward thresholds, store credit, and the Wallet line only when this phone has a wallet the server can
 * issue to. `t` gives the wording in force for a key (the admin edits these); the placeholders are filled here.
 */
export function joinBenefits(
  o: { welcome: number; card: number; credit: number; side: number; main: number; wallet: 'Apple Wallet' | 'Google Wallet' | null },
  t: (k: 'join.benefit_welcome' | 'join.benefit_rate' | 'join.benefit_rewards' | 'join.benefit_credit' | 'join.benefit_wallet') => string,
): string[] {
  const fill = (s: string) => s.replace(/\{(welcome|card|credit|side|main|wallet)\}/g, (_, k: string) => (k === 'wallet' ? o.wallet ?? '' : num(o[k as 'welcome' | 'card' | 'credit' | 'side' | 'main'])));
  const out: string[] = [];
  if (o.welcome > 0) out.push(fill(t('join.benefit_welcome')));
  out.push(fill(t('join.benefit_rate')), fill(t('join.benefit_rewards')), fill(t('join.benefit_credit')));
  if (o.wallet) out.push(fill(t('join.benefit_wallet')));
  return out;
}

export const welcomeLine = (points: number): string => (points > 0 ? `${num(points)} welcome points arrive with your first order` : '');

/** A claim's answer in words: the receipt's points, and the welcome points when they came with it. */
export function claimMessage(r: { pointsAdded: number; receiptPoints?: number; welcomePoints?: number }): string {
  const welcome = r.welcomePoints ?? 0;
  const receipt = r.receiptPoints ?? r.pointsAdded - welcome;
  return welcome > 0 ? `${num(receipt)} points added from your receipt, plus ${num(welcome)} welcome points` : `${num(receipt)} points added from your receipt`;
}

/** Why POST /api/me/email didn't send a link, in words. */
export function emailErrorText(e: unknown): string {
  const x = err(e);
  if (x?.code === 'bad_email') return "That email address doesn't look right.";
  if (x?.code === 'email_confirmed') return 'That email is already confirmed on your account.';
  if (x?.status === 429) return "That's three links in the last hour. Try again later, or check your spam folder.";
  if (x?.status === 0) return x.message;
  return 'Could not send the link. Please try again.';
}

export type ConfirmResult = { ok: true; email: string; pointsAdded: number; already: boolean };
/** What the confirmation page says: the answer of POST /api/email/confirm, or why the link didn't work. */
export function confirmText(r: ConfirmResult | null, e: unknown): { title: string; body: string; good: boolean } {
  if (r) {
    if (r.already) return { title: 'Already confirmed', body: `${r.email} is confirmed on your account.`, good: true };
    return { title: 'Email confirmed', body: r.pointsAdded > 0 ? `Thanks. ${num(r.pointsAdded)} points are on your card.` : `Thanks. ${r.email} is confirmed on your account.`, good: true };
  }
  const x = err(e);
  if (x?.status === 410) return { title: 'That link has expired', body: 'Links last 7 days. Send a new one from Account in the app.', good: false };
  if (x?.status === 404) return { title: "That link doesn't work", body: 'It may have been copied only in part. Send a new one from Account in the app.', good: false };
  if (x?.code === 'email_changed') return { title: 'Your email has changed since', body: 'This link was for an earlier address. Send a new one from Account in the app.', good: false };
  if (x?.status === 0) return { title: "Can't reach Tikkas", body: 'Check your connection and open the link again.', good: false };
  return { title: 'Something went wrong', body: 'Open the link again, or send a new one from Account in the app.', good: false };
}

/** Why Get my card back (docs/23 §3.7) didn't go through, in words; `noAccount` offers joining instead. */
export function cardBackErrorText(e: unknown): { text: string; noAccount: boolean } {
  const x = err(e);
  const said = x?.message?.trim();
  if (x?.code === 'bad_phone') return { text: 'Enter a UK mobile number, for example 07700 900123', noAccount: false };
  if (x?.code === 'wallet_off' || x?.code === 'wallet_not_configured') return { text: 'Wallet cards open soon. Your card is in the app, on the Card tab.', noAccount: false };
  if (x?.code === 'no_account') return { text: said || "There's no Rewards Club account with that number. Join and you'll get a card.", noAccount: true };
  if (x?.code === 'wrong_code') return { text: said || "That code isn't right. Check the text and try again.", noAccount: false };
  if (x?.code === 'code_expired') return { text: said || 'That code has expired. Go back and send a new one.', noAccount: false };
  if (x?.code === 'code_locked') return { text: said || 'Too many tries with that code. Go back and send a new one.', noAccount: false };
  if (x && (x.status === 429 || x.status === 0) && said) return { text: said, noAccount: false };
  return { text: 'Something went wrong. Please try again.', noAccount: false };
}
